/* --------------------------------------------------------------------------
   efx-quote-dialog.js - T-QUOTE-DIALOG 2026-09-01.
   Opens the "Get a quote" enquiry form as a small modal window instead of
   navigating to it. Draft theme #131236954194 only.

   PROGRESSIVE ENHANCEMENT - read this before changing anything here.
   The canonical destination is the PAGE (templates/page.get-a-quote.liquid).
   Every trigger keeps a real href to it. This file only intercepts the click.
   If it never loads, never parses, or the browser has no HTMLDialogElement,
   nothing is intercepted and the browser follows the href. There is no code
   path in which a "Get a quote" click does nothing.

   WHAT OPENS IT
     [data-efx-quote]                        - the hook other lanes bind to
                                               (the mega-menu quote CTA uses it)
     any same-origin link to the quote page  - the header utility link and the
                                               mobile drawer link, untouched
   Modifier-clicks, middle-clicks and target=_blank are left alone on purpose:
   "open in a new tab" must still open the page.

   WHY NATIVE <dialog>: showModal, ::backdrop and inert all measured supported
   here (logs/t-quote/tqd-probe1.json). showModal puts the panel in the top
   layer and makes the rest of the document inert - that IS the focus trap, and
   it is the browser's, not a hand-rolled one. What showModal does not do, and
   this file therefore does: lock the background scroll without shifting the
   page, return focus to the trigger, animate, and mark the siblings inert
   explicitly so the behaviour does not depend on one engine's interpretation.

   SUBMISSION - decided by measurement, not preference (2026-09-01):
     - A background fetch POST to /contact answers 403 "Verifying your
       connection" (logs/t-quote/tqd-probe2.json section B). The legacy
       snippets/ef-quote-modal.liquid does exactly that and then shows a success
       message anyway. That is a lie to the buyer and it is not repeated here.
     - So the form keeps Shopify's native navigation POST. The page reloads and
       comes back with ?contact_posted=true, and this file reopens the dialog on
       the confirmation - which is Shopify's own server-rendered success state,
       fetched from the same Liquid template (section A of the same probe proved
       ?contact_posted=true drives form.posted_successfully?), not a message we
       invented.
     - If the post comes back WITHOUT that parameter, the send did not go
       through: the buyer's answers are restored from the draft efx-quote.js
       parked before submitting, and they are told plainly that it did not send.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var d = document.getElementById('efqd');
  if (!d || typeof d.showModal !== 'function') return; /* links keep working */

  var body = d.querySelector('[data-efqd-body]');
  /* A preview can outlive its cookie or share cookies with another open theme.
     Keep both the fragment and the real-page fallback on this rendered theme.
     Published pages keep their ordinary URLs. */
  function sameThemeUrl(value, fresh) {
    var theme = window.Shopify && window.Shopify.theme;
    if (!theme || (theme.role !== 'unpublished' && theme.role !== 'development')) return value;
    try {
      var url = new URL(value, location.href);
      if (url.origin === location.origin && /^\d+$/.test(String(theme.id))) {
        url.searchParams.set('preview_theme_id', String(theme.id));
        if (fresh) url.searchParams.set('_ef_quote', String(Date.now()));
        return url.pathname + url.search + url.hash;
      }
    } catch (e) { /* keep the server-rendered destination */ }
    return value;
  }
  var PAGE = sameThemeUrl(d.getAttribute('data-page') || '/pages/get-a-quote');
  var FRAG = sameThemeUrl(d.getAttribute('data-frag'));
  var CSS = d.getAttribute('data-css');
  var JS = d.getAttribute('data-js');
  var WIZ = d.getAttribute('data-wiz');
  var POST_FLAG = 'efqd:posted';

  var lastTrigger = null;
  var state = 'empty';      /* empty | loading | form | done | error */
  var closing = false;
  var inerted = [];
  var downOnBackdrop = false;
  var pending = null;

  function reduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* -- 1. trigger discovery ---------------------------------------------- */
  function triggerFor(node) {
    if (!node || !node.closest || node.closest('[data-efqd-page]')) return null;
    var hook = node.closest('[data-efx-quote]');
    if (hook) return hook;
    var a = node.closest('a[href]');
    if (!a) return null;
    var u;
    try { u = new URL(a.getAttribute('href'), location.href); } catch (e) { return null; }
    if (u.origin !== location.origin) return null;
    if (u.pathname === '/pages/get-a-quote') return a;
    if (u.searchParams.get('view') === 'get-a-quote') return a;
    return null;
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var node = (e.target && e.target.nodeType === 1) ? e.target : (e.target && e.target.parentElement);
    var t = triggerFor(node);
    if (!t) return;
    if (t.getAttribute && t.getAttribute('target') === '_blank') return;
    e.preventDefault();
    open(t, { force: state === 'done' || state === 'error' });
  });

  /* The /pages/get-a-quote record is store data and still awaits the owner
     (T-QUOTE section 6.1). Until it is created that URL 404s, so while it is
     missing point the links at the URL that does render - a right-click "open
     in new tab" then works too. Only rewritten when Liquid told us the record
     is absent; the moment the owner creates it, data-page is the real URL and
     nothing is touched. */
  function repointDeadLinks() {
    if (PAGE.indexOf('view=get-a-quote') === -1) return 0;
    var n = 0;
    var links = document.querySelectorAll('a[href$="/pages/get-a-quote"], a[href*="/pages/get-a-quote?"]');
    for (var i = 0; i < links.length; i++) {
      var u;
      try { u = new URL(links[i].getAttribute('href'), location.href); } catch (e) { continue; }
      if (u.pathname !== '/pages/get-a-quote') continue;
      links[i].setAttribute('href', PAGE);
      links[i].setAttribute('data-efqd-repointed', '1');
      n++;
    }
    return n;
  }

  /* -- 2. scroll lock ----------------------------------------------------- */
  /* html carries an inline overflow-y:scroll, so the scrollbar is always
     present. Hiding it would reclaim its width and shift the whole page - the
     lock rule therefore pays that width back as padding. */
  function lock() {
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--efqd-sbw', (sbw > 0 ? sbw : 0) + 'px');
    document.documentElement.classList.add('efqd-lock');
  }
  function unlock() {
    document.documentElement.classList.remove('efqd-lock');
    document.documentElement.style.removeProperty('--efqd-sbw');
  }

  function inertBackground() {
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === d || el.inert) continue;
      el.inert = true;
      inerted.push(el);
    }
  }
  function unInertBackground() {
    for (var i = 0; i < inerted.length; i++) inerted[i].inert = false;
    inerted = [];
  }

  /* -- 3. open / close ---------------------------------------------------- */
  function open(trigger, opts) {
    opts = opts || {};
    if (trigger) lastTrigger = trigger;
    if (!d.open) {
      lock();
      d.showModal();
      inertBackground();
    }
    requestAnimationFrame(function () { d.classList.add('is-in'); });
    try { d.focus(); } catch (e) { /* noop */ }
    ensure(opts);
  }

  function close() {
    if (!d.open || closing) return;
    closing = true;
    d.classList.remove('is-in');
    window.setTimeout(function () {
      closing = false;
      if (d.open) d.close();
    }, reduced() ? 0 : 220);
  }

  d.addEventListener('cancel', function (e) { e.preventDefault(); close(); }); /* Escape */
  d.addEventListener('close', function () {
    /* Closing this window - by X, by Escape, by the backdrop - must not be able
       to cost the buyer their answers. The dialog keeps its own DOM, so
       reopening in the same page already restores everything; this covers the
       case where they close it and then leave the page. */
    if (window.EFQuoteForm && typeof window.EFQuoteForm.save === 'function' &&
        state === 'form') { window.EFQuoteForm.save(); }
    d.classList.remove('is-in');
    unlock();
    unInertBackground();
    restoreFocus();
  });

  /* Focus has to go back where it came from. Two things get in the way:
     the un-inert above only takes effect for the NEXT frame, and the mobile
     drawer that holds one of the triggers runs its own focus handling. So:
     try, check on the next frame, try once more, and if the trigger is gone or
     unfocusable fall back to whatever control opens the container it lives in
     (the burger owns the drawer via aria-controls) rather than dumping the user
     at the top of the document. Measured failing at 390/360 before this,
     logs/t-quote/tqd-m1.json. */
  /* Do not PREDICT focusability, test it. The mobile drawer's quote link has
     layout boxes and is in the document and still refuses focus (measured,
     logs/t-quote/tqd-focusdiag.json: canFocusLink false, canFocusBurger true),
     so any is-it-visible heuristic gets this wrong. Call focus and read
     document.activeElement back. */
  function tryFocus(el) {
    if (!el || !document.contains(el)) return false;
    try { el.focus(); } catch (e) { return false; }
    return document.activeElement === el;
  }
  /* When the trigger itself will not take focus, land on the control that opens
     the thing the trigger lives in - the burger owns #efx-drawer via
     aria-controls - so the user comes back where they were instead of at the
     top of the document. */
  function opener(el) {
    var host = el && el.closest ? el.closest('[id]') : null;
    while (host) {
      var btn = document.querySelector('[aria-controls="' + host.id + '"]');
      if (btn) return btn;
      host = host.parentElement ? host.parentElement.closest('[id]') : null;
    }
    return null;
  }
  function restoreFocus() {
    if (tryFocus(lastTrigger)) return;
    if (tryFocus(opener(lastTrigger))) return;
    /* the un-inert above and the drawer's own close both land next frame */
    requestAnimationFrame(function () {
      if (!tryFocus(lastTrigger)) tryFocus(opener(lastTrigger));
    });
  }
  d.addEventListener('mousedown', function (e) { downOnBackdrop = (e.target === d); });
  d.addEventListener('click', function (e) {
    var el = (e.target && e.target.nodeType === 1) ? e.target : null;
    if (el && el.closest('[data-efqd-page]') && e.button === 0 &&
        !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      window.location.assign(PAGE);
      return;
    }
    if (el && el.closest('[data-efqd-close]')) { close(); return; }
    if (e.target === d && downOnBackdrop) close();
    downOnBackdrop = false;
  });

  /* -- 4. content --------------------------------------------------------- */
  function loadOnce(tag, attrs, marker) {
    return new Promise(function (res) {
      if (document.querySelector('[' + marker + ']')) return res(true);
      var el = document.createElement(tag);
      Object.keys(attrs).forEach(function (k) { el.setAttribute(k, attrs[k]); });
      el.setAttribute(marker, '');
      el.onload = function () { res(true); };
      el.onerror = function () { res(false); };
      document.head.appendChild(el);
    });
  }
  function loadCss() {
    if (!CSS) return Promise.resolve(false);
    return loadOnce('link', { rel: 'stylesheet', href: CSS }, 'data-efqd-css');
  }
  function loadFormJs() {
    if (!JS) return Promise.resolve(false);
    return loadOnce('script', { src: JS }, 'data-efqd-js');
  }
  /* T-QUOTE-WIZARD: fetched in the same parallel batch as the markup, never
     after it. efx-quote-wizard.js puts .efq-wizard on <html> at parse time and
     that class is what hides the non-current steps - land it first and the long
     form never paints for a frame inside the modal. If it fails, the class is
     never set, and what is injected is the complete long form. */
  function loadWizJs() {
    if (!WIZ) return Promise.resolve(false);
    return loadOnce('script', { src: WIZ }, 'data-efqd-wiz');
  }

  /* The wizard pins the panel to a fixed height so nothing can move between
     steps (efx-quote.css section 14). Every content swap must drop it again, or
     the confirmation - which has no steps - would sit in a 780 px frame. */
  function unpin() { d.classList.remove('efqd-wiz'); }

  function showLoading(msg) {
    unpin();
    body.scrollTop = 0;
    body.innerHTML = '<div class="efqd__load"><span class="efqd__spin" aria-hidden="true"></span>' +
      '<span role="status">' + msg + '</span></div>';
  }

  /* Never a fake success and never a dead end: if the fragment will not load,
     say so and hand over the real page. */
  function showFailedToLoad() {
    /* A header hover may already have prepared the standalone page under an
       older preview session. Give this recovery navigation a fresh draft URL. */
    PAGE = sameThemeUrl(PAGE, true);
    state = 'error';
    unpin();
    body.scrollTop = 0;
    body.innerHTML = '<div class="efqd__load"><span role="status">The form did not load just now.</span>' +
      '<a class="efx-btn efx-btn--sm" data-efqd-page href="' + PAGE + '">Open it on its own page</a></div>';
  }

  function ensure(opts) {
    var wantPosted = !!opts.posted;
    if (!opts.force && !wantPosted && (state === 'form' || state === 'done')) return;
    if (pending) return pending;
    state = 'loading';
    showLoading(wantPosted ? 'Loading your confirmation' : 'Opening the quote form');

    /* Stylesheet AND behaviour are fetched alongside the markup, not after it.
       efx-quote.js puts .efq-js on <html>, which is what hides the conditional
       fireplace-type question; injecting the markup first would paint that
       question for a frame and then snatch it away. Loading the script first
       also keeps the no-JS contract exact: if it fails, .efq-js is never set and
       every question stays visible, which is the correct degraded form. */
    var url = FRAG + (wantPosted ? '&contact_posted=true' : '');
    pending = Promise.all([
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); }),
      loadCss(),
      loadFormJs(),
      loadWizJs()
    ]).then(function (res) {
      var holder = document.createElement('div');
      holder.innerHTML = res[0];
      var frag = holder.querySelector('[data-efq-fragment]');
      if (!frag) throw new Error('fragment marker missing');
      body.scrollTop = 0;
      body.innerHTML = '';
      unpin();
      body.appendChild(frag);
      state = frag.querySelector('[data-efq-done]') ? 'done' : 'form';
      body.classList.toggle('efqd-is-done', state === 'done');
      /* R7: a trigger that knows its category (data-efq-cat="outdoor-kitchen") hands it to the form;
         efx-quote.js turns it into the first tile before the wizard boots. */
      var catSrc = lastTrigger && lastTrigger.getAttribute ? (lastTrigger.getAttribute('data-efq-cat') || '') : '';
      if (!catSrc && lastTrigger && lastTrigger.href) { try { catSrc = new URL(lastTrigger.href, location.href).searchParams.get('cat') || ''; } catch (e) { catSrc = ''; } }
      var formEl = frag.querySelector('#efq-form');
      if (catSrc && formEl) formEl.setAttribute('data-efq-cat', catSrc);
      if (opts.failed) showSendFailed(frag);
      if (window.EFQuoteForm && typeof window.EFQuoteForm.boot === 'function') {
        window.EFQuoteForm.boot();
      }
      bindSubmit();
      pending = null;
    })['catch'](function () {
      pending = null;
      showFailedToLoad();
    });
    return pending;
  }

  /* The honest failure state. Shopify's contact endpoint sends the buyer back
     without contact_posted=true when it refuses the post, so we know it did not
     send - we just do not know why, and we do not guess. efx-quote.js's one-shot
     draft restore puts every answer back, so nothing has to be retyped. */
  function showSendFailed(frag) {
    var note = document.createElement('div');
    note.className = 'efq-note efq-note--err';
    note.setAttribute('role', 'alert');
    note.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/>' +
      '<path d="M12 8v5M12 16.5v.01"/></svg><span><strong>That did not send.</strong> ' +
      'Everything you typed is still here — check the email address and send it again, or call ' +
      '<a href="tel:+19496197824">(949) 619-7824</a>.</span>';
    var form = frag.querySelector('#efq-form');
    if (form) form.insertBefore(note, form.firstChild);
    else frag.insertBefore(note, frag.firstChild);
  }

  /* The native post navigates. Leave a one-shot mark so the page we come back
     to knows to reopen the dialog - and only when the browser actually let the
     submit through (efx-quote.js cancels it when the honeypot is filled). */
  function bindSubmit() {
    var form = body.querySelector('#efq-form');
    if (!form || form.getAttribute('data-efqd-submit') === '1') return;
    form.setAttribute('data-efqd-submit', '1');
    /* The form emits this only after validation and saving the chosen action,
       before Shopify may pause or replay the native submission. */
    form.addEventListener('efq:submit-prepared', function () {
      try { sessionStorage.setItem(POST_FLAG, '1'); } catch (err) { /* private mode */ }
    });
  }

  /* -- 5. coming back from the native post -------------------------------- */
  function resumeAfterPost() {
    var flag = null;
    try { flag = sessionStorage.getItem(POST_FLAG); } catch (e) { return; }
    if (!flag) return;
    try { sessionStorage.removeItem(POST_FLAG); } catch (e) { /* noop */ }

    var params = new URLSearchParams(location.search);
    var ok = params.get('contact_posted') === 'true';
    open(null, { posted: ok, failed: !ok, force: true });

    if (ok) {
      params['delete']('contact_posted');
      var q = params.toString();
      try {
        history.replaceState(null, '', location.pathname + (q ? '?' + q : '') + location.hash);
      } catch (e) { /* noop */ }
    }
  }

  function boot() {
    repointDeadLinks();
    resumeAfterPost();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.EFQuoteDialog = {
    open: function (trigger) { open(trigger || null, { force: state === 'done' || state === 'error' }); },
    close: close,
    state: function () { return state; }
  };
})();
